/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, OnInit, signal} from '@angular/core';
import {completeCopyModal} from '../common/util/complete-copy-modal';
import {firstValueFrom, Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import template from './line-item-copy-modal.component.html';
import {nextCopyTitle} from '../../components/util/next-copy-title';
import {Uuid} from '../../components/util/uuid';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {deferSignalViewWrite} from '../common/util/template-state';
import {LineItemApiService} from './line-item-api.service';

export interface LineItemCopyResult {
  lineItemId: string;
  destination: boolean;
}

@Component({
  standalone: false,
  selector: 'adn-line-item-copy-modal',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineItemCopyModalComponent implements OnInit {
  model: any;

  readonly onCopied = new Subject<LineItemCopyResult>();

  lineItem: any = {};
  isMarketplace = false;
  copy = {targeting: true, creatives: true};
  destination = true;
  disabled = false;
  validationErrors: any[] = [];
  readonly viewRevision = signal(0);

  constructor(
    public bsModalRef: BsModalRef,
    private readonly lineItemApi: LineItemApiService,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any
  ) {
    this.isMarketplace = LocalNetworkProfile.isMarketplacePlatform();
  }

  ngOnInit(): void {
    this.lineItem = _.cloneDeep(this.model || {});
    this.lineItem.name = nextCopyTitle(this.model?.name || '');
    this.lineItem.userState = 'PROPOSED';
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  copyCampaign(): void {
    this.disabled = true;
    this.validationErrors = [];
    this.adnModalLoading.applyModalLoading();
    const newId = Uuid.generate();
    firstValueFrom(this.lineItemApi.copyLineItem(
      this.lineItem,
      newId,
      this.copy.targeting,
      this.copy.creatives,
      true
    )).then(() => {
      // Defer disabled reset — sync then during modal CD races attr.disabled (NG0100).
      deferSignalViewWrite(this.viewRevision, () => {
        this.disabled = false;
        this.adnModalLoading.removeModalLoading();
        completeCopyModal(this.bsModalRef, this.onCopied, {destination: this.destination, lineItemId: newId});
      });
    }, (errors: any) => {
      deferSignalViewWrite(this.viewRevision, () => {
        this.disabled = false;
        this.validationErrors = (errors && errors.data && errors.data.errors) || [];
        this.adnModalLoading.removeModalLoading();
      });
    });
  }
}
